import { useEffect, useMemo, useState } from 'react'
import { Phone, PhoneOff } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { channelName, displayName, useSocialStore } from '@/store/social'
import { useVoiceStore } from '@/store/voice'
import { startRingtone, stopRingtone } from '@/lib/ringtone'
import { describeError } from '@/lib/auth'
import { desktop } from '@/lib/desktop'
import { avatarUrl } from '@/lib/avatar'
import { ChannelType } from '@/types/api'
import { Avatar } from '../Avatar'
import { GroupAvatar } from '../GroupAvatar'
import styles from './IncomingCall.module.css'

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

/** Входящий звонок: показывается, пока нам «звонят» (ringing содержит нас), с кнопками принять и отклонить. */
export function IncomingCall() {
  const me = useSocialStore((s) => s.currentUserId)
  const calls = useSocialStore((s) => s.calls)
  const channels = useSocialStore((s) => s.channels)
  const callStates = useSocialStore((s) => s.callStates)
  const declineCall = useSocialStore((s) => s.declineCall)
  const connection = useVoiceStore((s) => s.connection)
  const join = useVoiceStore((s) => s.join)
  const audiooutput = useVoiceStore((s) => s.prefs.audiooutput)
  const outputVolume = useVoiceStore((s) => s.prefs.outputVolume)
  const setView = useAppStore((s) => s.setView)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const incoming = useMemo(() => Object.values(calls).find((c) => me && c.ringing.includes(me) && connection?.channelId !== c.channel_id), [calls, me, connection])
  const channel = incoming ? channels[incoming.channel_id] : undefined

  const incomingId = incoming?.channel_id
  useEffect(() => {
    if (incomingId) startRingtone({ sinkId: audiooutput || undefined, volume: 0.6 * outputVolume })
    else stopRingtone()
    // в десктопе мигаем иконкой в панели задач, пока окно свёрнуто
    desktop?.attention(Boolean(incomingId))
    return () => {
      stopRingtone()
      desktop?.attention(false)
    }
  }, [incomingId, audiooutput, outputVolume])

  if (!incoming || !channel) return null
  const group = channel.type === ChannelType.GroupDM
  const inCall = Object.values(callStates[incoming.channel_id] ?? {})
  const caller = inCall[0] ? (channel.recipients ?? []).find((u) => u.id === inCall[0]?.user_id) : undefined
  const title = group ? channelName(channel) : caller ? displayName(caller) : channelName(channel)
  const subtitle = group ? `${caller ? displayName(caller) : 'Участник'} начал(а) звонок в группе` : 'Входящий звонок'

  const accept = async () => {
    stopRingtone()
    setBusy(true)
    setError(null)
    try {
      setView({ kind: 'home', channelId: incoming.channel_id })
      await join(null, incoming.channel_id)
    } catch (err) {
      setError(describeError(err).message)
      if (me && useSocialStore.getState().calls[incoming.channel_id]?.ringing.includes(me)) {
        startRingtone({ sinkId: audiooutput || undefined, volume: 0.6 * outputVolume })
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-label={`Входящий звонок: ${title}`}>
      <div className={styles.card}>
        {/* в группе заголовок — название группы, а не звонящий: его аватар там был бы не к месту */}
        {group ? (
          <GroupAvatar recipients={channel.recipients} size={80} className={styles.avatar} />
        ) : (
          <Avatar initial={title.slice(0, 1).toUpperCase()} color={caller ? colorOf(caller.id) : '#5865F2'} size={80} className={styles.avatar} src={caller ? avatarUrl(caller.id, caller.avatar) : undefined} decoration={caller?.avatar_decoration_data} />
        )}
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.subtitle}>{subtitle}</p>
        {error ? <p className={styles.error}>{error}</p> : null}
        <div className={styles.actions}>
          <button type="button" className={styles.decline} onClick={() => void declineCall(incoming.channel_id)} aria-label="Отклонить">
            <PhoneOff size={22} />
          </button>
          <button type="button" className={styles.accept} disabled={busy} onClick={() => void accept()} aria-label="Принять">
            <Phone size={22} />
          </button>
        </div>
      </div>
    </div>
  )
}
